import type { Question } from '../../data/schemas';
import type { OptionOrders } from '../../types/session';
import { createSeededRandom } from './seededRandom';

/**
 * Seeded Fisher-Yates-Shuffle. Nicht `array.sort(() => Math.random() - 0.5)` (FR-SHUFFLE-002).
 * Arbeitet auf einer Kopie, mutiert das übergebene Array nicht (DAT-003).
 */
export function fisherYatesShuffle<T>(items: readonly T[], random: () => number): T[] {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    const current = shuffled[i] as T;
    const swap = shuffled[j] as T;
    shuffled[i] = swap;
    shuffled[j] = current;
  }
  return shuffled;
}

/**
 * Erzeugt für jede Frage einer Session eine gemischte Reihenfolge der Options-IDs (FR-SHUFFLE-003).
 * Nur die Optionen innerhalb einer Frage werden gemischt, nicht die Fragenreihenfolge (FR-SHUFFLE-001).
 */
export function shuffleOptionsForSession(
  questions: readonly Question[],
  seed: string,
): OptionOrders {
  const random = createSeededRandom(seed);
  const orders: OptionOrders = {};
  for (const question of questions) {
    const optionIds = question.options.map((option) => option.id);
    orders[question.id] = fisherYatesShuffle(optionIds, random);
  }
  return orders;
}

/** Ordnet die Antwortoptionen einer Frage gemäß gespeicherter Session-Reihenfolge an. */
export function applyOptionOrder(
  question: Question,
  order: readonly string[] | undefined,
): Question['options'] {
  if (!order || order.length === 0) return question.options;
  const optionsById = new Map(question.options.map((option) => [option.id, option]));
  const ordered: Question['options'] = [];
  for (const optionId of order) {
    const option = optionsById.get(optionId);
    if (option) ordered.push(option);
  }
  // Fallback: falls die gespeicherte Reihenfolge unvollständig ist, restliche Optionen anhängen.
  for (const option of question.options) {
    if (!ordered.includes(option)) ordered.push(option);
  }
  return ordered;
}
